// The product's confirm dialog. Staff UI never uses the browser's own
// window.confirm/alert/prompt (navguard's TestNoNativeDialogs enforces it):
// every question is this ui.Modal, rendered once per page by
// templates.ConfirmDialogHTML and driven through Alpine.store("confirm").
//
// Two ways in:
//   - markup: data-confirm="question" on the element that issues the htmx
//     request (the form, or the button/link carrying hx-post/hx-get);
//     handleHtmxConfirm holds the request back until the user confirms.
//   - script: await window.appConfirm("question") resolves true/false.
//
// Unlike window.confirm, the dialog does not block the page while it asks:
// a swap can replace the asking element in the meantime (handleHtmxConfirm
// reports that instead of dropping the request silently), and a form's
// submitter is not sent with a confirmed request (htmx forgets the clicked
// button when focus moves into the dialog), so a confirmed form must not
// depend on its submit button's name/value or formaction.

/**
 * A click on the backdrop this soon after opening is the second click of a
 * double-click on the asking button, not an answer.
 */
export const DISMISS_GRACE_MS = 400;

export class ConfirmDialog {
  public open = false;
  public message = "";
  private resolve: ((ok: boolean) => void) | null = null;
  private returnFocus: HTMLElement | null = null;
  private openedAt = 0;

  /** Opens the dialog. A question still pending is answered "no" first. */
  public ask(message: string, now = Date.now()): Promise<boolean> {
    // A question asked while one is open keeps the first opener: the element
    // focused now is the dialog's own button.
    const opener = this.open
      ? this.returnFocus
      : typeof document !== "undefined"
        ? (document.activeElement as HTMLElement | null)
        : null;
    this.settle(false);
    this.returnFocus = opener;
    this.message = message;
    this.open = true;
    this.openedAt = now;
    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  /** Closes the dialog with the user's answer. */
  public answer(ok: boolean) {
    this.open = false;
    const el = focusTarget(this.returnFocus);
    this.returnFocus = null;
    if (el) el.focus();
    this.settle(ok);
  }

  /** The backdrop, the close button and click-outside: a "no", except during
   * the double-click grace period right after opening. */
  public dismiss(now = Date.now()) {
    if (!this.open || now - this.openedAt < DISMISS_GRACE_MS) return;
    this.answer(false);
  }

  private settle(ok: boolean) {
    const resolve = this.resolve;
    this.resolve = null;
    if (resolve) resolve(ok);
  }
}

type Focusable = Pick<HTMLElement, "focus" | "isConnected" | "getClientRects" | "closest">;

/**
 * Where focus goes back to after the dialog: the opener while it is still
 * shown, else the trigger of the menu it sat in (a context-menu item is hidden
 * once its menu closes), else nowhere.
 */
export function focusTarget<T extends Focusable>(el: T | null): T | null {
  if (!el || !el.isConnected) return null;
  if (el.getClientRects().length > 0) return el;
  const trigger = el.closest("[x-data]")?.querySelector("[aria-haspopup]") as T | null | undefined;
  return trigger && trigger.getClientRects().length > 0 ? trigger : null;
}

/**
 * Escape answers the dialog "no" and goes no further: registered on window in
 * the capture phase, it runs before every @keydown.escape.window handler, so
 * the side panel or modal underneath does not close with it.
 */
export function handleConfirmEscape(e: KeyboardEvent, dialog: ConfirmDialog) {
  if (e.key !== "Escape" || !dialog.open) return;
  e.stopImmediatePropagation();
  e.preventDefault();
  dialog.answer(false);
}

type HtmxConfirmDetail = {
  elt: Element;
  issueRequest: (skipConfirmation?: boolean) => void;
};

/**
 * htmx:confirm listener for data-confirm. Only the element that issues the
 * request is read (no inheritance from ancestors), and a request another
 * htmx:confirm handler already held back is left to that handler. If the
 * element was swapped away while the dialog was open, htmx would drop the
 * request without a word, so gone() reports it instead.
 */
export function handleHtmxConfirm(
  e: Event,
  ask: (message: string) => Promise<boolean>,
  gone: () => void = () => {},
) {
  if (e.defaultPrevented) return;
  const detail = (e as CustomEvent<HtmxConfirmDetail>).detail;
  const message = detail?.elt?.getAttribute("data-confirm");
  if (!message) return;
  e.preventDefault();
  void ask(message).then((ok) => {
    if (!ok) return;
    if (!detail.elt.isConnected) {
      gone();
      return;
    }
    detail.issueRequest(true);
  });
}
